import React, { useRef } from "react";
import PropTypes from "prop-types";

import { TinySliderNav, SlideItem } from "@euroland/react-tiny-slider";

import "tiny-slider/dist/tiny-slider.css";
import { classNames } from "../utils";
import Tag from "./ui/Tag";
import Badge from "./ui/Badge";
import useInstruments, { useInstrument } from "../hooks/useInstruments";
import { i18n } from "@euroland/libs";
import { formatDate } from "@euroland/libs";
import { useEffect } from "react";
import { getCurrencyLabel } from "../store/utils";
import { useCompanySettings } from "../hooks/useCompanySettings";
import { POSITIVE_CHANGE_NUMBER_FORMAT } from "../constant/common";
import { formatNumberByInstrument } from "../utils/format-number";
// import dayjs from "dayjs";

const CheckedIcon = () => (
  <svg
    className="slide-check-icon"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="20" height="20" rx="4" />
    <path d="M4 10L8 14L16 6" strokeWidth="2" />
  </svg>
);

const UnCheckIcon = () => (
  <svg
    className="slide-uncheck-icon"
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <rect x="1" y="1" width="18" height="18" rx="3" strokeWidth="2" />
  </svg>
);

export default function MultipleTickerSlider() {
  const [{ instruments }] = useInstruments();
  const [{ selectedInstrument }, { onChangeSelectedInstrument }] = useInstrument();
  const ref = useRef();
  useEffect(() => {
    ref.current = selectedInstrument;
  }, [selectedInstrument]);

  const handleClickTickerItem = id => {
    if (Object.keys(ref.current).length <= 1 && id in ref.current) return;
    onChangeSelectedInstrument?.(id);
  };
  let activeOrder = instruments.findIndex(
    instrument => instrument.instrumentId === +Object.keys(selectedInstrument)[0]
  );
  activeOrder = activeOrder !== -1 ? activeOrder : 0;
  return (
    <div className="MultipleTickerSlider">
      <TinySliderNav
        isRtl={window.appSettings.isRtl}
        activeOrder={activeOrder}
        settings={{ gutter: 0 }}
        role="listbox"
        aria-label={i18n.translate("tickerAriaLabel")}
        aria-multiselectable="true"
        aria-orientation="horizontal"
      >
        {instruments.map((ticker, index) => (
          <TickerItem
            sliderIndex={index}
            key={ticker.instrumentId}
            active={ticker.instrumentId in selectedInstrument}
            onClick={() => handleClickTickerItem(ticker.instrumentId)}
            ticker={ticker}
            isSingleTicket={instruments.length === 1}
          />
        ))}
      </TinySliderNav>
    </div>
  );
}

/**
 *
 * @param {{
 *  active: boolean,
 *  isSingleTicket: boolean,
 *  ticker: import("../..").IInstrument,
 *  onClick: () => void,
 * }} props
 * @returns
 */
export function TickerItem({ active, ticker, onClick, isSingleTicket, sliderIndex }) {
  const settings = useCompanySettings();
  const positiveChangeFormat = settings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT;

  return (
    <SlideItem
      className={classNames("TickerItem", { active })}
      onClick={onClick}
      role="option"
      aria-selected={active}
      sliderIndex={sliderIndex}
      // aria-label={`${ticker.ticker} ${i18n.translate("startingDateTickerVictor")} ${dayjs(ticker.startingDate).format('YYYY MMMM DD')}`}
    >
      <div className="TickerItem__container">
        <div className="title-wrapper">
          {!isSingleTicket && (
            <div className={classNames("TickerItem__checkbox", { checked: active })}>
              {active ? <CheckedIcon /> : <UnCheckIcon />}
            </div>
          )}
          <Badge active={ticker.marketStatus === "Open"} />
          <h4 title={ticker.ticker}>{ticker.ticker}</h4>
          <Tag label={ticker.marketAbbreviation} aria-label={ticker.marketName} role="note" />
        </div>
        <div className="value-index">
          <div className="value-wrapper">
            <h2 className="value">{formatNumberByInstrument(ticker.last, ticker.instrumentId)}</h2>
            <p className="currency" aria-label={ticker.currency} role="note">
              {getCurrencyLabel(ticker.currencyCode)}
            </p>
          </div>
          <div
            className={classNames("change-wrapper", {
              "indicator--up": ticker.change > 0,
              "indicator--down": ticker.change < 0,
              "indicator--natural": ticker.change === 0,
            })}
          >
            <p
              className={classNames("change-value", {
                "change-up": ticker.change >= 0,
                "change-down": ticker.change < 0,
              })}
            >
              {formatNumberByInstrument(ticker.change, ticker.instrumentId, {
                positiveFormatPattern: positiveChangeFormat,
              })}
            </p>
            <p
              className={classNames("change-percent", {
                "change-up": ticker.changePercentage >= 0,
                "change-down": ticker.changePercentage < 0,
              })}
            >
              (
              {formatNumberByInstrument(ticker.changePercentage, ticker.instrumentId, {
                positiveFormatPattern: positiveChangeFormat,
                decimalDigits: settings.format.decimalDigits,
              })}
              %)
            </p>
          </div>
          {!isSingleTicket && (
            <div className="TickerItem__date">
              <span className="TickerItem__date--label">{i18n.translate("startingDateTickerVictor")}</span>
              <span
                className="TickerItem__date--time"
                aria-label={formatDate(ticker.startingDate, "fullDate")}
                role="note"
              >
                {formatDate(ticker.startingDate, "shortDate")}
              </span>
            </div>
          )}
        </div>
      </div>
    </SlideItem>
  );
}

MultipleTickerSlider.propTypes = {};
TickerItem.prototype = {
  ticker: PropTypes.exact({
    id: PropTypes.number,
    default: PropTypes.bool,
    tickerName: PropTypes.string,
    marketName: PropTypes.string,
    currencySign: PropTypes.string,
    numberOfTrade: PropTypes.number,
  }),
  onClick: PropTypes.func,
};
